/**
 * Cartas oficiales en Word (.docx).
 *
 * Es la misma carta que sale en PDF —mismo texto, mismas tablas, mismas
 * medidas—, pero en un archivo que se puede editar antes de enviarlo. Para eso
 * lo piden: corregir un nombre o agregar una línea sin volver a generar el
 * corte.
 *
 * El .docx se arma a mano: se escribe el OOXML y se comprime con JSZip. No hay
 * librería de Word en el proyecto y la carta es un documento fijo —un membrete,
 * dos tablas y un pie—, así que agregar una dependencia para esto no se
 * justificaba. Las medidas salen de CARTA, las mismas que se midieron sobre el
 * PDF oficial: los dos archivos salen calcados.
 *
 * Unidades de Word: las medidas van en twips (1 pt = 20 twips), las imágenes en
 * EMU (1 pt = 12700 EMU) y los tamaños de letra en medios puntos.
 */
import JSZip from 'jszip'
import {
   CARTA,
   FIRMA,
   MEMBRETE,
   cronometro,
   dinero,
   fechaLarga,
   may,
   SEDES_PIE
} from './reporteIntegracion'

const NS =
   'xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" ' +
   'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" ' +
   'xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing" ' +
   'xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" ' +
   'xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture"'

const XML = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'

/** Carta vertical, 612 x 792 pt. */
const PAGINA = { ancho: 612, alto: 792 }

/** Los colores de Word van sin el numeral: solo los seis dígitos. */
const NEGRO = CARTA.colorTexto.replace('#', '')
const RELLENO = CARTA.rellenoEncabezado.replace('#', '')

const tw = (pt) => Math.round(pt * 20)
const emu = (pt) => Math.round(pt * 12700)
/** Los tamaños de letra de Word van en medios puntos. */
const mp = (pt) => Math.round(pt * 2)

function esc(texto) {
   return (texto === null || texto === undefined ? '' : String(texto))
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
}

// ─── piezas del documento ────────────────────────────────────────────────────

/**
 * Un trozo de texto con formato. El orden de las etiquetas dentro de w:rPr no
 * es libre: Word rechaza el documento entero si no respeta el del esquema.
 */
function texto(contenido, o = {}) {
   const tam = o.tam || 12
   const rPr =
      (o.negrita ? '<w:b/>' : '') +
      `<w:color w:val="${NEGRO}"/>` +
      `<w:sz w:val="${mp(tam)}"/><w:szCs w:val="${mp(tam)}"/>`

   return (
      `<w:r><w:rPr>${rPr}</w:rPr>` +
      `<w:t xml:space="preserve">${esc(contenido)}</w:t></w:r>`
   )
}

/**
 * Un párrafo. Las opciones se declaran en puntos, igual que las del PDF, y acá
 * se pasan a twips. `alto` fija la altura exacta del renglón, que es como se
 * reserva el espacio de la firma.
 */
function parrafo(trozos, o = {}) {
   const espaciado =
      `<w:spacing w:before="${tw(o.antes || 0)}" ` +
      `w:after="${tw(o.despues || 0)}"` +
      (o.alto
         ? ` w:line="${tw(o.alto)}" w:lineRule="exact"/>`
         : ' w:line="240" w:lineRule="auto"/>')

   const pPr =
      (o.saltoAntes ? '<w:pageBreakBefore/>' : '') +
      (o.juntoAlSiguiente ? '<w:keepNext/>' : '') +
      (o.tabDerecha
         ? `<w:tabs><w:tab w:val="right" w:pos="${tw(o.tabDerecha)}"/></w:tabs>`
         : '') +
      espaciado +
      (o.sangriaIzq || o.sangriaDer
         ? `<w:ind w:left="${tw(o.sangriaIzq || 0)}" ` +
           `w:right="${tw(o.sangriaDer || 0)}"/>`
         : '') +
      (o.alineacion ? `<w:jc w:val="${o.alineacion}"/>` : '')

   return `<w:p><w:pPr>${pPr}</w:pPr>${[].concat(trozos).join('')}</w:p>`
}

/** Atajo para el caso corriente: un párrafo con un solo texto. */
function linea(contenido, o = {}) {
   return parrafo(texto(contenido, o), o)
}

/**
 * Celda de dinero al estilo contable del original: el signo pegado a la
 * izquierda y el importe alineado a la derecha, no "$80.00" junto. En el PDF
 * son dos columnas dentro de la celda; acá, un tabulador derecho al filo del
 * texto, que es lo que da el mismo dibujo sin partir la celda en dos.
 */
function parrafoMonto(valor, ancho, tam, negrita) {
   return parrafo(
      [
         texto('$', { tam, negrita }),
         '<w:r><w:tab/></w:r>',
         texto(dinero(valor), { tam, negrita })
      ],
      { tabDerecha: ancho - CARTA.padCeldaH * 2 }
   )
}

/**
 * Una celda. `padV` sube el relleno vertical de los encabezados, que en el
 * original son más altos que las filas de datos.
 */
function celda(ancho, parrafos, o = {}) {
   const padV = o.padV === undefined ? CARTA.padCeldaV : o.padV
   const tcPr =
      `<w:tcW w:w="${tw(ancho)}" w:type="dxa"/>` +
      (o.relleno
         ? `<w:shd w:val="clear" w:color="auto" w:fill="${RELLENO}"/>`
         : '') +
      '<w:tcMar>' +
      `<w:top w:w="${tw(padV)}" w:type="dxa"/>` +
      `<w:left w:w="${tw(CARTA.padCeldaH)}" w:type="dxa"/>` +
      `<w:bottom w:w="${tw(padV)}" w:type="dxa"/>` +
      `<w:right w:w="${tw(CARTA.padCeldaH)}" w:type="dxa"/>` +
      '</w:tcMar><w:vAlign w:val="center"/>'

   return `<w:tc><w:tcPr>${tcPr}</w:tcPr>${[].concat(parrafos).join('')}</w:tc>`
}

/** Celda de encabezado: fondo gris azulado, negrita y centrada. */
function celdaTh(ancho, renglones, tam) {
   const parrafos = []
      .concat(renglones)
      .map((t) => linea(t, { tam, negrita: true, alineacion: 'center' }))

   return celda(ancho, parrafos, { relleno: true, padV: CARTA.padEncabezadoV })
}

/**
 * Bordes de la rejilla. El original mide 0.4 pt, pero Word no acepta cualquier
 * grosor: lo ajusta al valor mas cercano de su lista y 3 octavos (0.375 pt)
 * cae en el cuarto de punto, la linea mas fina que dibuja. Esa linea sale del
 * PDF como un rectangulo de 0.24 pt y los visores la pierden: la tabla se ve
 * sin lineas. Medio punto —4 octavos— es el primer grosor que sobrevive a la
 * exportacion, y en papel no se distingue del original.
 */
const BORDE = `w:val="single" w:sz="4" w:space="0" w:color="${NEGRO}"`

const LADOS = ['top', 'left', 'bottom', 'right', 'insideH', 'insideV']

const REJILLA =
   '<w:tblBorders>' +
   LADOS.map((lado) => `<w:${lado} ${BORDE}/>`).join('') +
   '</w:tblBorders>'

/**
 * Arma la tabla. La primera fila lleva w:tblHeader: es lo que hace que el
 * encabezado se vuelva a dibujar cuando la tabla sigue en la página siguiente,
 * igual que el headerRows del PDF.
 *
 * `sinCortarFilas` es el dontBreakRows del listado: una fila no se parte entre
 * dos páginas.
 */
function tabla(anchos, filas, o = {}) {
   const total = anchos.reduce((suma, ancho) => suma + ancho, 0)
   const grid = anchos.map((a) => `<w:gridCol w:w="${tw(a)}"/>`).join('')

   const cuerpo = filas
      .map((celdas, i) => {
         const trPr =
            (o.sinCortarFilas ? '<w:cantSplit/>' : '') +
            (i === 0 ? '<w:tblHeader/>' : '')

         return `<w:tr>${
            trPr ? `<w:trPr>${trPr}</w:trPr>` : ''
         }${celdas}</w:tr>`
      })
      .join('')

   return (
      '<w:tbl><w:tblPr>' +
      `<w:tblW w:w="${tw(total)}" w:type="dxa"/>` +
      // Sin tblInd explícito Word corre la tabla a la izquierda del margen.
      '<w:tblInd w:w="0" w:type="dxa"/>' +
      REJILLA +
      '<w:tblLayout w:type="fixed"/>' +
      `</w:tblPr><w:tblGrid>${grid}</w:tblGrid>${cuerpo}</w:tbl>`
   )
}

// ─── las dos tablas de la carta ──────────────────────────────────────────────

/** Listado nominal: una fila por becario. */
function tablaListado(sede, porcentaje) {
   const an = CARTA.colsListado

   const filas = [
      celdaTh(an[0], 'N°', 8) +
         celdaTh(an[1], 'CARNET', 8) +
         celdaTh(an[2], 'NOMBRE', 8) +
         celdaTh(an[3], 'CARRERA', 8) +
         celdaTh(an[4], 'FACULTAD', 8) +
         celdaTh(an[5], 'SEDE', 8) +
         // Tres renglones explícitos, como en el original.
         celdaTh(an[6], ['MONTO', 'INTEGRACIÓN', porcentaje], 8)
   ]

   sede.detalle.forEach((d) => {
      filas.push(
         celda(an[0], linea(String(d.no), { tam: 8, alineacion: 'center' })) +
            celda(
               an[1],
               linea(may(d.carnet), { tam: 8, alineacion: 'center' })
            ) +
            celda(an[2], linea(may(d.nombreCompleto), { tam: 8 })) +
            // La carrera va un punto más chica: es la columna que más se
            // desborda.
            celda(
               an[3],
               linea(may(d.carreraNombre), { tam: 7, alineacion: 'center' })
            ) +
            celda(
               an[4],
               linea(may(d.facultadCodigo), { tam: 8, alineacion: 'center' })
            ) +
            celda(an[5], linea(may(d.sede), { tam: 8, alineacion: 'center' })) +
            celda(an[6], parrafoMonto(d.montoIntegracion, an[6], 8))
      )
   })

   filas.push(
      celda(an[0], linea('', { tam: 8 }), { relleno: true }) +
         celda(an[1], linea('', { tam: 8 }), { relleno: true }) +
         celda(an[2], linea('', { tam: 8 }), { relleno: true }) +
         celda(an[3], linea('TOTALES', { tam: 8, negrita: true }), {
            relleno: true
         }) +
         celda(an[4], linea('', { tam: 8 }), { relleno: true }) +
         celda(an[5], linea('', { tam: 8 }), { relleno: true }) +
         celda(an[6], parrafoMonto(sede.total, an[6], 8, true), {
            relleno: true
         })
   )

   return tabla(an, filas, { sinCortarFilas: true })
}

/** Resumen por carreras, con la cuota completa y la parte que aporta la D.I. */
function tablaResumen(sede, porcentaje, centro) {
   const an = CARTA.colsResumen

   const filas = [
      celdaTh(an[0], 'N°', 9) +
         celdaTh(an[1], `CARRERAS UNICAES ${centro.toUpperCase()}`, 9) +
         celdaTh(an[2], 'N° DE ALUMNOS', 9) +
         celdaTh(an[3], 'VALOR CUOTA UNICAES', 9) +
         celdaTh(an[4], `VALOR CUOTA ${porcentaje} (D.I)`, 9) +
         celdaTh(an[5], 'MONTO', 9)
   ]

   sede.carreras.forEach((c, i) => {
      filas.push(
         celda(an[0], linea(String(i + 1), { tam: 9, alineacion: 'center' })) +
            celda(an[1], linea(may(c.carrera), { tam: 9 })) +
            celda(
               an[2],
               linea(String(c.becados), { tam: 9, alineacion: 'center' })
            ) +
            celda(an[3], parrafoMonto(c.cuotaUnicaes, an[3], 9)) +
            celda(an[4], parrafoMonto(c.cuotaIntegracion, an[4], 9)) +
            celda(an[5], parrafoMonto(c.monto, an[5], 9))
      )
   })

   filas.push(
      celda(an[0], linea('', { tam: 9 }), { relleno: true }) +
         celda(
            an[1],
            linea('TOTALES', { tam: 9, negrita: true, alineacion: 'center' }),
            { relleno: true }
         ) +
         celda(
            an[2],
            linea(String(sede.becados), {
               tam: 9,
               negrita: true,
               alineacion: 'center'
            }),
            { relleno: true }
         ) +
         celda(an[3], linea('', { tam: 9 }), { relleno: true }) +
         celda(an[4], linea('', { tam: 9 }), { relleno: true }) +
         celda(an[5], parrafoMonto(sede.total, an[5], 9, true), {
            relleno: true
         })
   )

   return tabla(an, filas)
}

// ─── imágenes ────────────────────────────────────────────────────────────────

/** Mide una imagen: hace falta para conservar su proporción. */
function medir(blob) {
   return new Promise((resolve) => {
      const url = URL.createObjectURL(blob)
      const img = new Image()

      img.onload = () => {
         resolve({ ancho: img.naturalWidth, alto: img.naturalHeight })
         URL.revokeObjectURL(url)
      }
      img.onerror = () => {
         resolve(null)
         URL.revokeObjectURL(url)
      }
      img.src = url
   })
}

/**
 * Trae una imagen del sitio con sus bytes y su tamaño real. Devuelve null si no
 * está: la carta se arma igual sin ella, como hace el PDF.
 */
async function cargarImagen(url) {
   try {
      const respuesta = await fetch(url)
      if (!respuesta.ok) return null

      const blob = await respuesta.blob()
      const medida = await medir(blob)
      if (!medida || !medida.ancho) return null

      return {
         bytes: await blob.arrayBuffer(),
         extension: (url.split('.').pop() || 'png').toLowerCase(),
         ancho: medida.ancho,
         alto: medida.alto
      }
   } catch (e) {
      return null
   }
}

/**
 * Imagen incrustada en un párrafo. El ancho manda y el alto sale de la
 * proporción original, igual que en el PDF.
 */
function imagen(rel, img, ancho, id, nombre) {
   const alto = (ancho * img.alto) / img.ancho
   const ext = `cx="${emu(ancho)}" cy="${emu(alto)}"`

   return (
      '<w:r><w:drawing>' +
      `<wp:inline distT="0" distB="0" distL="0" distR="0"><wp:extent ${ext}/>` +
      '<wp:effectExtent l="0" t="0" r="0" b="0"/>' +
      `<wp:docPr id="${id}" name="${nombre}"/>` +
      '<wp:cNvGraphicFramePr>' +
      '<a:graphicFrameLocks noChangeAspect="1"/></wp:cNvGraphicFramePr>' +
      '<a:graphic><a:graphicData uri="http://schemas.openxmlformats.org/' +
      'drawingml/2006/picture"><pic:pic>' +
      `<pic:nvPicPr><pic:cNvPr id="${id}" name="${nombre}"/>` +
      '<pic:cNvPicPr/></pic:nvPicPr>' +
      `<pic:blipFill><a:blip r:embed="${rel}"/>` +
      '<a:stretch><a:fillRect/></a:stretch></pic:blipFill>' +
      `<pic:spPr><a:xfrm><a:off x="0" y="0"/><a:ext ${ext}/></a:xfrm>` +
      '<a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr>' +
      '</pic:pic></a:graphicData></a:graphic></wp:inline>' +
      '</w:drawing></w:r>'
   )
}

// ─── encabezado y pie ────────────────────────────────────────────────────────

/** El membrete se repite en todas las páginas, como en el original. */
function encabezado(membrete) {
   const logo = membrete
      ? parrafo(imagen('rId1', membrete, CARTA.membreteAncho, 10, 'membrete'), {
           alineacion: 'center'
        })
      : ''

   return (
      `${XML}<w:hdr ${NS}>` +
      logo +
      linea('http://www.catolica.edu.sv', {
         tam: 11,
         alineacion: 'center',
         antes: 4
      }) +
      '</w:hdr>'
   )
}

/**
 * Pie con las direcciones de las dos sedes, en dos columnas centradas. Van en
 * una tabla sin bordes: es como Word pone dos bloques lado a lado dentro del
 * pie.
 */
function pieDePagina() {
   const ancho = (PAGINA.ancho - CARTA.margenIzq - CARTA.margenDer) / 2

   const columna = (titulo, lineas) =>
      celda(
         ancho,
         [
            linea(titulo, {
               tam: 11,
               negrita: true,
               alineacion: 'center',
               antes: 8
            })
         ].concat(
            lineas.map((t) => linea(t, { tam: 9, alineacion: 'center' }))
         ),
         { padV: 0 }
      )

   const fila = SEDES_PIE.map((s) => columna(s.titulo, s.lineas)).join('')

   const sinBordes =
      '<w:tbl><w:tblPr>' +
      `<w:tblW w:w="${tw(ancho * 2)}" w:type="dxa"/>` +
      '<w:tblInd w:w="0" w:type="dxa"/><w:tblBorders>' +
      LADOS.map(
         (lado) => `<w:${lado} w:val="none" w:sz="0" w:space="0"/>`
      ).join('') +
      '</w:tblBorders><w:tblLayout w:type="fixed"/></w:tblPr>' +
      `<w:tblGrid><w:gridCol w:w="${tw(ancho)}"/>` +
      `<w:gridCol w:w="${tw(ancho)}"/></w:tblGrid><w:tr>${fila}</w:tr></w:tbl>`

   // Word exige un párrafo detrás de la última tabla de cualquier parte del
   // documento; sin él da el archivo por dañado y no lo abre.
   return `${XML}<w:ftr ${NS}>${sinBordes}${linea('', { tam: 1 })}</w:ftr>`
}

// ─── la carta ────────────────────────────────────────────────────────────────

/** Carta de una sede, calcada de la que emite el PDF. */
function documentoCarta(reporte, sede, firma) {
   const esIlobasco = sede.sede === 'ILOBASCO'
   const lugar = esIlobasco ? 'Ilobasco' : 'Santa Ana'
   const centro = esIlobasco ? 'Centro Regional de Ilobasco' : 'Sede Santa Ana'
   const porcentaje = `${Number(reporte.porcentajeIntegracion)}%`
   const promocion = sede.detalle.length ? sede.detalle[0].cohorte : ''

   // El bloque de la carta va sangrado respecto de las tablas: en el documento
   // oficial arranca en x=56.6 y cierra en ~555, mientras que las tablas se
   // abren hasta 42.8 y 575.3.
   const cuerpo = (o) => ({
      ...o,
      sangriaIzq: CARTA.sangriaCuerpoIzq,
      sangriaDer: CARTA.sangriaCuerpoDer
   })

   const contenido =
      linea(
         `${lugar}, ${fechaLarga(reporte)}`,
         cuerpo({ alineacion: 'right' })
      ) +
      linea('Señores', cuerpo({ antes: 1.8 })) +
      linea('Dirección de Integración', cuerpo({ negrita: true })) +
      linea('Presente', cuerpo({})) +
      linea('Estimados Señores:', cuerpo({ antes: 13.9 })) +
      linea(
         'Con base al convenio, remito informe correspondiente al mes de ' +
            `${reporte.mesNombre.toLowerCase()} en el ciclo ${
               reporte.ciclo
            } de becados de la ` +
            `Universidad Católica de El Salvador, ${centro}.`,
         cuerpo({ alineacion: 'both', antes: 13.8 })
      ) +
      linea('PROGRAMA DE BECAS DIRECCIÓN DE INTEGRACIÓN', {
         tam: 10,
         negrita: true,
         alineacion: 'center',
         antes: 13.9
      }) +
      linea(`PROMOCIÓN ${promocion}`, {
         tam: 10,
         negrita: true,
         alineacion: 'center',
         antes: 1.6,
         despues: 3
      }) +
      tablaListado(sede, porcentaje) +
      linea(`Resumen por carreras ${centro}`, {
         negrita: true,
         saltoAntes: true,
         sangriaIzq: 42,
         despues: 16
      }) +
      tablaResumen(sede, porcentaje, centro) +
      // El espacio para sellar y firmar es un renglón vacío de alto fijo, no un
      // margen: Word se come el margen superior cuando el bloque cae al inicio
      // de una página y ahí el nombre queda pegado al membrete. Va amarrado al
      // que sigue para que el hueco y la firma no terminen en páginas
      // distintas.
      parrafo('', {
         alto: firma ? CARTA.espacioFirma - 40 : CARTA.espacioFirma,
         juntoAlSiguiente: true
      }) +
      (firma
         ? parrafo(imagen('rId4', firma, CARTA.firmaAncho, 20, 'firma'), {
              alineacion: 'center',
              juntoAlSiguiente: true
           })
         : '') +
      linea(CARTA.firmante, {
         tam: 10,
         alineacion: 'center',
         juntoAlSiguiente: true
      }) +
      linea(CARTA.cargoFirmante, { tam: 10, alineacion: 'center' })

   const seccion =
      '<w:sectPr>' +
      '<w:headerReference w:type="default" r:id="rId2"/>' +
      '<w:footerReference w:type="default" r:id="rId3"/>' +
      `<w:pgSz w:w="${tw(PAGINA.ancho)}" w:h="${tw(PAGINA.alto)}"/>` +
      `<w:pgMar w:top="${tw(CARTA.margenSup)}" w:right="${tw(
         CARTA.margenDer
      )}" w:bottom="${tw(CARTA.margenInf)}" w:left="${tw(CARTA.margenIzq)}" ` +
      `w:header="${tw(CARTA.membreteMargenSup)}" w:footer="${tw(20)}" ` +
      'w:gutter="0"/></w:sectPr>'

   return (
      `${XML}<w:document ${NS}><w:body>${contenido}${seccion}` +
      '</w:body></w:document>'
   )
}

// ─── empaquetado ─────────────────────────────────────────────────────────────

const RELS =
   'http://schemas.openxmlformats.org/officeDocument/2006/relationships'
const PAQUETE = 'application/vnd.openxmlformats-officedocument.wordprocessingml'
const PAQUETE_RELS =
   'http://schemas.openxmlformats.org/package/2006/relationships'

/**
 * Hoja de estilos mínima: fija la letra del documento —Times New Roman de 12,
 * en negro— y que los párrafos no lleven el espacio de más que Word
 * agrega por su cuenta.
 */
/**
 * Sin esta parte Word abre el documento en "Modo de compatibilidad": al no
 * encontrar la marca de version supone que viene de Word 2007 y desactiva lo
 * que agrego despues. El 15 es el modo de Word 2013 en adelante, que es el
 * ultimo que define el formato.
 *
 * doNotAutoCompressPictures es la casilla "no comprimir las imagenes del
 * archivo" guardada en el documento: evita que Word recomprima el membrete
 * cuando alguien edite y guarde la carta. Ojo, no manda sobre el PDF que
 * exporta Word: ahi la resolucion la decide la opcion de la maquina.
 */
const AJUSTES =
   `${XML}<w:settings ${NS}><w:compat>` +
   `<w:compatSetting w:name="compatibilityMode" ` +
   `w:uri="http://schemas.microsoft.com/office/word" w:val="15"/>` +
   `</w:compat><w:doNotAutoCompressPictures/></w:settings>`

const ESTILOS =
   `${XML}<w:styles ${NS}><w:docDefaults><w:rPrDefault><w:rPr>` +
   '<w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" ' +
   'w:cs="Times New Roman"/>' +
   `<w:color w:val="${NEGRO}"/><w:sz w:val="24"/><w:szCs w:val="24"/>` +
   '</w:rPr></w:rPrDefault><w:pPrDefault><w:pPr>' +
   '<w:spacing w:before="0" w:after="0" w:line="240" w:lineRule="auto"/>' +
   '</w:pPr></w:pPrDefault></w:docDefaults>' +
   '<w:style w:type="paragraph" w:default="1" w:styleId="Normal">' +
   '<w:name w:val="Normal"/><w:qFormat/></w:style></w:styles>'

/** El paquete tiene que declarar el tipo de cada parte que lleva dentro. */
function tiposDeContenido(extensiones) {
   const tipos = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg' }
   const partes = [
      ['document.xml', 'document.main+xml'],
      ['styles.xml', 'styles+xml'],
      ['settings.xml', 'settings+xml'],
      ['header1.xml', 'header+xml'],
      ['footer1.xml', 'footer+xml']
   ]

   return (
      `${XML}<Types xmlns="http://schemas.openxmlformats.org/package/2006/` +
      'content-types"><Default Extension="rels" ContentType="application/' +
      'vnd.openxmlformats-package.relationships+xml"/>' +
      '<Default Extension="xml" ContentType="application/xml"/>' +
      extensiones
         .map(
            (e) =>
               `<Default Extension="${e}" ContentType="${
                  tipos[e] || 'image/png'
               }"/>`
         )
         .join('') +
      partes
         .map(
            ([nombre, tipo]) =>
               `<Override PartName="/word/${nombre}" ` +
               `ContentType="${PAQUETE}.${tipo}"/>`
         )
         .join('') +
      '</Types>'
   )
}

/**
 * Arma el .docx de una carta: las partes obligatorias del paquete OOXML más el
 * documento, el encabezado, el pie y las imágenes.
 */
function empaquetar(reporte, sede, membrete, firma) {
   const zip = new JSZip()
   const extensiones = []

   const rel = (id, tipo, destino) =>
      `<Relationship Id="${id}" Type="${RELS}/${tipo}" Target="${destino}"/>`

   const relaciones = (lista) =>
      `${XML}<Relationships xmlns="${PAQUETE_RELS}">${lista.join(
         ''
      )}</Relationships>`

   const delDocumento = [
      rel('rId1', 'styles', 'styles.xml'),
      rel('rId5', 'settings', 'settings.xml'),
      rel('rId2', 'header', 'header1.xml'),
      rel('rId3', 'footer', 'footer1.xml')
   ]

   if (membrete) {
      extensiones.push(membrete.extension)
      zip.file(`word/media/membrete.${membrete.extension}`, membrete.bytes)
      zip.file(
         'word/_rels/header1.xml.rels',
         relaciones([
            rel('rId1', 'image', `media/membrete.${membrete.extension}`)
         ])
      )
   }

   if (firma) {
      if (extensiones.indexOf(firma.extension) < 0) {
         extensiones.push(firma.extension)
      }
      zip.file(`word/media/firma.${firma.extension}`, firma.bytes)
      delDocumento.push(rel('rId4', 'image', `media/firma.${firma.extension}`))
   }

   zip.file('[Content_Types].xml', tiposDeContenido(extensiones))
   zip.file(
      '_rels/.rels',
      relaciones([rel('rId1', 'officeDocument', 'word/document.xml')])
   )
   zip.file('word/_rels/document.xml.rels', relaciones(delDocumento))
   zip.file('word/settings.xml', AJUSTES)
   zip.file('word/styles.xml', ESTILOS)
   zip.file('word/header1.xml', encabezado(membrete))
   zip.file('word/footer1.xml', pieDePagina())
   zip.file('word/document.xml', documentoCarta(reporte, sede, firma))

   return zip.generateAsync({
      type: 'blob',
      mimeType: `${PAQUETE}.document`,
      compression: 'DEFLATE'
   })
}

function descargar(blob, nombre) {
   const url = window.URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.href = url
   a.download = nombre
   a.click()
   window.URL.revokeObjectURL(url)
}

/**
 * Una carta por sede, igual que el PDF: cada sede remite la suya.
 *
 * `conFirma` decide si se estampa la firma escaneada de la Dirección
 * Administrativa. Va apagado por defecto por lo mismo que en el PDF: que un
 * botón emita cartas ya firmadas es una decisión de la universidad, no del
 * reporte. Además la imagen no viene en el repositorio.
 */
export async function generarCartasWord(reporte, conFirma = false) {
   const reloj = cronometro()

   const membrete = await cargarImagen(MEMBRETE)
   const firma = conFirma ? await cargarImagen(FIRMA) : null
   reloj.etapa('carga de imágenes')

   for (const sede of reporte.resumenPorSede) {
      const blob = await empaquetar(reporte, sede, membrete, firma)
      reloj.etapa(`armado ${sede.sede} (${sede.detalle.length} filas)`)

      descargar(blob, `BECARIOS UNICAES ${sede.sede} ${reporte.mesNombre}.docx`)
   }

   reloj.fin(
      'Cartas Word generadas',
      `${reporte.resumenPorSede.length} sede(s) · ${reporte.detalle.length} becarios`
   )
}
